import type { LaunchIdentityRecord } from '../../shared/types/launch-identity';
import type { LaunchIdentityRepository } from '../interfaces/repositories';
import { projectLaunchIdentity } from './governed-records';
import type { JsonStore } from './json-store';
import { STORAGE_KEYS } from './keys';
import { toLaunchIdentityDto } from './persistence-dto';
import { createWriteQueue, type WriteQueue } from './write-queue';

/**
 * Who launched the one in-flight sitting (D-054(2), Slice R).
 *
 * Exactly one, beside the one mission snapshot: this is the evidence the resume guard needs to
 * decide whose that snapshot is, not a history of who has played. It is written when a sitting
 * starts and cleared wherever the snapshot is cleared.
 */
export function createLocalLaunchIdentityRepository(store: JsonStore): LaunchIdentityRepository {
  return {
    async get(): Promise<LaunchIdentityRecord | null> {
      return store.read(STORAGE_KEYS.launchIdentity, projectLaunchIdentity);
    },

    async save(record: LaunchIdentityRecord): Promise<void> {
      const projected = toLaunchIdentityDto(record);
      // A record that would be refused on the way back in is not written at all. Half an identity
      // is not a weaker identity — it is a different one, and resuming into a guess is exactly
      // what D-036(4) exists to prevent.
      if (projected === null) return;
      // The identity is the other half of the one sitting slot, so it gets the same
      // replace-or-erase and verified-clear semantics as the snapshot (S1-5).
      store.replaceSlot(STORAGE_KEYS.launchIdentity, projected);
    },

    async clear(): Promise<void> {
      store.clearSlot(STORAGE_KEYS.launchIdentity);
    },
  };
}

/**
 * The identity's writes, ordered on the same chain as the snapshot's (see `write-queue.ts`).
 *
 * The two records describe one sitting, so a clear of either must not outrun a pending write of
 * the other: a snapshot with no identity beside it is refused by the guard, and a child loses the
 * rounds they played.
 */
export function withSerialisedIdentityWrites(
  inner: LaunchIdentityRepository,
  queue: WriteQueue = createWriteQueue(),
): LaunchIdentityRepository {
  return {
    get: () => inner.get(),
    save: (record) => queue(() => inner.save(record)),
    clear: () => queue(() => inner.clear()),
  };
}
